import React from 'react';
import { getIn, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';

import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import LobMultiSelectWithChipsFormik from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import BaseConfigurationSection from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Sections/BaseConfigurationSection';
import ConfiguredFieldsSection from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Sections/ConfiguredFieldsSection';
import type { ConfigurationField } from '~/components/SystemConfiguration/IncidentConfigurationPage/types';
import SystemFieldConfigurationCard from '~/components/SystemConfiguration/PolicyConfiguration/PolicyConfigurationScreen/SystemFieldConfigurationCard/SystemFieldConfigurationCard';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type { DocumentType } from '~/components/types/document-type';

export const CLIVE_INSTRUCTIONS_MAX_LENGTH = 1000;

export const DOCUMENT_TYPE_FORMIK_IDS = {
  KEY: 'key',
  DISPLAY_NAME: 'display_name',
  DESCRIPTION: 'description',
  IS_ALL_LOBS: 'is_all_lobs',
  LOBS: 'lobs',
  CLIVE_INSTRUCTIONS: 'clive_instructions',
  CONFIGURATION: 'configuration',
  CONFIGURED_FIELDS_PATH: 'configuration.configured_fields',
} as const;

const DOCUMENT_SYSTEM_FIELDS: ConfigurationField[] = [
  { id: 'document_name', desc: 'Document Name', type: 'string', active: true, mandatory: false },
  { id: 'document_date', desc: 'Document Date', type: 'date', active: true, mandatory: true },
  { id: 'document_date_received', desc: 'Date Received', type: 'date', active: true, mandatory: false },
  { id: 'exposure_ids', desc: 'Linked Exposures', type: 'multiselect', active: true, mandatory: true },
  { id: 'summary', desc: 'Summary', type: 'multiline_string', active: true, mandatory: false },
];

const hasLobScopeChanged = (
  isAllLobsNow: boolean,
  lobsNow: string[] | undefined,
  originalIsAllLobs: boolean | undefined,
  originalLobs: string[] | undefined
): boolean => {
  if (originalIsAllLobs === undefined) return false;
  if (Boolean(isAllLobsNow) !== Boolean(originalIsAllLobs)) return true;
  if (isAllLobsNow) return false;
  const a = (lobsNow ?? []).slice().sort();
  const b = (originalLobs ?? []).slice().sort();
  if (a.length !== b.length) return true;
  return a.some((v, i) => v !== b[i]);
};

interface DocumentTypeFormFieldsProps {
  isUpdateMode: boolean;
  originalIsAllLobs?: boolean;
  originalLobs?: string[];
  readOnly?: boolean;
}

const DocumentTypeFormFields: React.FC<DocumentTypeFormFieldsProps> = ({
  isUpdateMode,
  originalIsAllLobs,
  originalLobs,
  readOnly = false,
}) => {
  const { isSubmitting, values } = useFormikContext<DocumentType>();
  const isDisabled = isSubmitting || readOnly;

  useSetDefaultFieldsOnChange(
    getIn(values, DOCUMENT_TYPE_FORMIK_IDS.DISPLAY_NAME),
    {
      [DOCUMENT_TYPE_FORMIK_IDS.KEY]: isUpdateMode
        ? getIn(values, DOCUMENT_TYPE_FORMIK_IDS.KEY)
        : snakeCase(getIn(values, DOCUMENT_TYPE_FORMIK_IDS.DISPLAY_NAME)),
    },
    null,
    readOnly
  );

  const lobScopeChanged =
    !readOnly &&
    isUpdateMode &&
    hasLobScopeChanged(
      Boolean(getIn(values, DOCUMENT_TYPE_FORMIK_IDS.IS_ALL_LOBS)),
      getIn(values, DOCUMENT_TYPE_FORMIK_IDS.LOBS),
      originalIsAllLobs,
      originalLobs
    );

  return (
    <div className="grid gap-20">
      {lobScopeChanged ? (
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          withIcon
          note="Changing the line of business will affect which claims this document type appears on going forward. Existing documents are not retroactively re-routed."
        />
      ) : null}
      <div className="grid gap-20 pl-24">
        <div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            Details
          </Text>
          <div className="grid gap-24">
            <div className="grid grid-cols-2 gap-20">
              <TextFieldFormik
                id={DOCUMENT_TYPE_FORMIK_IDS.DISPLAY_NAME}
                label="Document Type Name"
                disabled={isDisabled}
              />
              <TextFieldFormik
                id={DOCUMENT_TYPE_FORMIK_IDS.KEY}
                label="Document Type Key"
                disabled={isDisabled || isUpdateMode}
              />
            </div>
            <TextFieldFormik id={DOCUMENT_TYPE_FORMIK_IDS.DESCRIPTION} label="Description" disabled={isDisabled} />
          </div>
        </div>
        <div>
          <Text variant={Text.VARIANTS.SM}>Add any specific instructions for Clive when analyzing the document</Text>
          <TextFieldFormik
            id={DOCUMENT_TYPE_FORMIK_IDS.CLIVE_INSTRUCTIONS}
            label="Clive's instructions"
            fullWidth
            multiline
            rows={3}
            disabled={isDisabled}
            inputProps={{ maxLength: CLIVE_INSTRUCTIONS_MAX_LENGTH }}
          />
        </div>
        <div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            Context
          </Text>
          <div className="grid grid-cols-2 gap-20">
            <LobMultiSelectWithChipsFormik
              lobsFieldId={DOCUMENT_TYPE_FORMIK_IDS.LOBS}
              allLobsFieldId={DOCUMENT_TYPE_FORMIK_IDS.IS_ALL_LOBS}
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>
      <BaseConfigurationSection title="Document System Fields" withActionsContainerFullWidth={false}>
        {DOCUMENT_SYSTEM_FIELDS.map((field) => (
          <SystemFieldConfigurationCard field={field} key={field.id} />
        ))}
      </BaseConfigurationSection>
      <ConfiguredFieldsSection
        fieldId={DOCUMENT_TYPE_FORMIK_IDS.CONFIGURED_FIELDS_PATH}
        hideCategories
        withoutRequiredForClaimClosing
        withoutRequired
        subtitle="If you want to capture additional information, you can add custom fields."
        forceDisabled={readOnly}
        excludeTypes={['location', 'contact'] as unknown as never[]}
      />
    </div>
  );
};

export default DocumentTypeFormFields;
